/**
 * WerkstattCraftView – Craft-Ansicht für die Werkstatt (NT-WERKSTATT-CRAFT-02). Kein Modal —
 * rendert wie WerkstattBetriebView.ts direkt in den Jobs-Content-Bereich mit Back-Button.
 * Erreichbar ausschließlich über den "TEILE CRAFTEN"-Link in WerkstattBetriebView.ts (kein
 * eigener Job-Card-Eintrag, kein Nav-Eintrag). Zeigt je Rezept Output, Input-Stückliste mit
 * Haben/Fehlt und die Zuweisung eines freien Mechaniker-Slots. Datenquelle ist dieselbe
 * GET /werkstatt/betrieb-Übersicht wie WerkstattBetriebView.ts (liefert bereits `recipes` +
 * `mechanics` mit).
 */

import { api, ApiError } from '../services/api.js';
import { formatItemKey } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';

const ROOT_ID = 'werkstatt-craft-root';

interface WerkstattRecipeView {
  id: string;
  outputItemKey: string;
  outputQty: number;
  tier: 'GENERISCH' | 'PREMIUM';
  durationSeconds: number;
  inputs: { itemKey: string; qty: number; have: number }[];
  canCraft: boolean;
}

interface WerkstattMechanicView {
  id: string;
  name: string;
  status: 'BUSY' | 'CRAFTING' | 'PAUSED' | 'IDLE';
}

interface WerkstattBetriebOverviewLite {
  mechanics: WerkstattMechanicView[];
  recipes: WerkstattRecipeView[];
}

interface WerkstattBetriebResponse {
  unlocked: boolean;
  overview: WerkstattBetriebOverviewLite | null;
}

let overview: WerkstattBetriebOverviewLite | null = null;
let loading = false;
let busy = false;
// Pro Rezept-ID der zuletzt gewählte Mechaniker (Select-State, reiner UI-Zustand).
let selectedMechanicByRecipe: Record<string, string> = {};

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtDuration(totalSeconds: number): string {
  const totalMinutes = Math.round(totalSeconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${String(h)}h ${String(m)}min`;
  if (h > 0) return `${String(h)}h`;
  return `${String(m)}min`;
}

/** Vor dem Wechsel in die View aufrufen (synchron, vor renderAll()). */
export function prepareWerkstattCraftView(): void {
  overview = null;
  busy = false;
  selectedMechanicByRecipe = {};
  loading = true;
}

/** Beim Verlassen der View (Zurück-Button) bzw. beim Unmount des gesamten Dashboards. */
export function resetWerkstattCraftViewState(): void {
  overview = null;
  loading = false;
  busy = false;
  selectedMechanicByRecipe = {};
}

/** Synchrones Markup für den Jobs-Content-Bereich — Daten kommen erst danach via mount(). */
export function renderWerkstattCraftViewShell(): string {
  return `<div id="${ROOT_ID}">${renderInner()}</div>`;
}

/** Nach dem DOM-Insert aufrufen: lädt die Daten und hängt die Listener an den Root-Container. */
export async function mountWerkstattCraftView(): Promise<void> {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  await reload();
  loading = false;
  render();
  attachListeners(root);
}

async function reload(): Promise<void> {
  try {
    const res = await api.get<WerkstattBetriebResponse>('/werkstatt/betrieb');
    overview = res.overview;
  } catch {
    showToast('Werkstatt-Rezepte konnten nicht geladen werden.', 'error');
  }
}

function freeMechanics(o: WerkstattBetriebOverviewLite): WerkstattMechanicView[] {
  return o.mechanics.filter((m) => m.status === 'IDLE');
}

function renderInputRow(input: { itemKey: string; qty: number; have: number }): string {
  const missing = input.have < input.qty;
  return `<span>${String(input.qty)}&times; ${esc(formatItemKey(input.itemKey))} (${String(input.have)} im Puffer)${missing ? ' <span class="werkstatt-missing-badge">FEHLT</span>' : ''}</span>`;
}

function renderRecipeCard(r: WerkstattRecipeView, mechanics: WerkstattMechanicView[]): string {
  const hasFreeMechanic = mechanics.length > 0;
  const canSubmit = r.canCraft && hasFreeMechanic;
  const selected = selectedMechanicByRecipe[r.id] ?? mechanics[0]?.id ?? '';

  return `
    <div class="werkstatt-customer-row${r.canCraft ? '' : ' werkstatt-customer-row--missing'}">
      <div class="werkstatt-customer-header">
        <span class="werkstatt-customer-band">${r.tier}</span>
        <span class="werkstatt-customer-title">${String(r.outputQty)}&times; ${esc(formatItemKey(r.outputItemKey)).toUpperCase()}</span>
      </div>
      <div class="werkstatt-customer-bom">
        ${r.inputs.map(renderInputRow).join('')}
      </div>
      <div class="werkstatt-customer-reward">
        <span class="dash-meta-hint">Craft-Zeit: ${fmtDuration(r.durationSeconds)}</span>
      </div>
      <div class="werkstatt-part-form">
        ${hasFreeMechanic
          ? `<select data-werkstatt-craft-mechanic-select="${esc(r.id)}" ${busy ? 'disabled' : ''}>
              ${mechanics.map((m) => `<option value="${esc(m.id)}" ${m.id === selected ? 'selected' : ''}>${esc(m.name)}</option>`).join('')}
            </select>`
          : '<span class="dash-meta-hint">Kein freier Mechaniker verfügbar.</span>'}
        <button class="pfand-betrieb-btn-gold" type="button" data-werkstatt-craft-start="${esc(r.id)}" ${busy || !canSubmit ? 'disabled' : ''}>
          CRAFTEN
        </button>
      </div>
    </div>
  `;
}

function renderInner(): string {
  if (loading) return '<p class="dash-empty">Rezepte werden geladen…</p>';
  if (!overview) return '<p class="dash-empty">Werkstatt noch nicht freigeschaltet.</p>';

  const mechanics = freeMechanics(overview);
  const generisch = overview.recipes.filter((r) => r.tier === 'GENERISCH');
  const premium = overview.recipes.filter((r) => r.tier === 'PREMIUM');

  return `
    <p class="dash-meta-hint">
      Ein Mechaniker craftet exklusiv — während er ein Teil baut, kann er keine Reparaturaufträge annehmen.
      Inputs werden sofort verbraucht, das fertige Teil landet nach der Craft-Zeit im Teile-Puffer.
    </p>
    ${mechanics.length === 0 ? '<p class="dash-meta-hint">Kein Mechaniker frei — alle sind beschäftigt, pausiert oder craften bereits.</p>' : ''}
    <h3 class="pfand-betrieb-section-title">GENERISCH</h3>
    <div class="werkstatt-customer-list">
      ${generisch.map((r) => renderRecipeCard(r, mechanics)).join('')}
    </div>
    <h3 class="pfand-betrieb-section-title">PREMIUM</h3>
    <div class="werkstatt-customer-list">
      ${premium.map((r) => renderRecipeCard(r, mechanics)).join('')}
    </div>
  `;
}

function render(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.innerHTML = renderInner();
}

function attachListeners(root: HTMLElement): void {
  root.addEventListener('click', (e) => {
    const target = e.target as Element;
    const startBtn = target.closest<HTMLButtonElement>('[data-werkstatt-craft-start]');
    if (startBtn !== null && !startBtn.disabled) {
      const recipeId = startBtn.dataset.werkstattCraftStart ?? '';
      if (recipeId) void handleStartCraft(recipeId);
    }
  });

  root.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    const select = target.closest<HTMLSelectElement>('[data-werkstatt-craft-mechanic-select]');
    if (select !== null) {
      const recipeId = select.dataset.werkstattCraftMechanicSelect ?? '';
      if (recipeId) selectedMechanicByRecipe = { ...selectedMechanicByRecipe, [recipeId]: select.value };
    }
  });
}

async function handleStartCraft(recipeId: string): Promise<void> {
  if (busy || !overview) return;
  const mechanics = freeMechanics(overview);
  const mechanicId = selectedMechanicByRecipe[recipeId] ?? mechanics[0]?.id;
  if (!mechanicId) { showToast('Kein Mechaniker verfügbar.', 'error'); return; }

  busy = true;
  render();
  try {
    await api.post('/werkstatt/craft/start', { mechanicId, recipeId });
    showToast('Mechaniker craftet.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Craft konnte nicht gestartet werden.', 'error');
  } finally {
    busy = false;
    render();
  }
}
